/**
 * Layout wrapper with Navbar for authenticated pages
 */

import React, { useState, useCallback } from 'react'

import { useNavigate, useLocation } from 'react-router-dom'

import { ImportProgressIndicator } from '@/modules/accounting/features/import/components/ImportProgressIndicator'
import { useImportFailureToast } from '@/modules/accounting/features/import/hooks/useImportFailureToast'
import { useInvalidateTransactionsOnSyncCompleted } from '@/modules/accounting/features/import/hooks/useInvalidateTransactionsOnSyncCompleted'
import { usePostSyncSummaryToast } from '@/modules/accounting/features/import/hooks/usePostSyncSummaryToast'
import { AuthModal } from '@/platform/auth/components/AuthModal'
import { SkipLink } from '@/shared/components/a11y/SkipLink'
import { DeletionWarningBanner } from '@/shared/components/DeletionWarningBanner'
import { ImpersonationBanner } from '@/shared/components/ImpersonationBanner'
import { WorkspaceBanner } from '@/platform/workspaces/components/WorkspaceBanner'
import { Navbar } from '@/shared/components/layout'
import { AppFooter } from '@/shared/components/layout/AppFooter'

import { resolvePageSlugForPath, resolveRouteForPageSlug } from './AuthenticatedLayout.routes'

export const AuthenticatedLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [authModalOpen, setAuthModalOpen] = useState(false)
  const [authModalMode, setAuthModalMode] = useState<'login' | 'register'>('login')

  // Issue #322 B.3 — subscribe to SSE `sync_completed` events and surface the
  // 4-counter post-sync resumen toast (no UI here; the hook owns the toast
  // dispatch). Lives at the layout level so any post-sync event reaches the
  // user regardless of which page they're on when the import finishes.
  usePostSyncSummaryToast()

  // Surface a global ERROR toast on every SSE `sync_failed` so a failed
  // background import is never silent (mirrors the completed-summary toast).
  // The toast body is the SAFE backend message — never raw SQL/PII.
  useImportFailureToast()

  // Issue #346 — invalidate transactions/balances/imports React Query caches
  // on every sync_completed so the user does not need to F5 the /transactions
  // page after an import finishes. Lives next to the toast hook for symmetry.
  useInvalidateTransactionsOnSyncCompleted()

  // Determine current page for navbar highlighting. Both directions are
  // derived from AUTHENTICATED_MAIN_NAV_ITEMS so a nav entry can never point at
  // a slug this shell does not know (see ./AuthenticatedLayout.routes).
  const getCurrentPage = useCallback(
    () => resolvePageSlugForPath(location.pathname),
    [location.pathname],
  )

  // Handle navigation from navbar
  const handleNavigate = useCallback(
    (page: string) => {
      navigate(resolveRouteForPageSlug(page))
    },
    [navigate],
  )

  // Handle opening auth modal
  const handleOpenAuthModal = useCallback((mode: 'login' | 'register') => {
    setAuthModalMode(mode)
    setAuthModalOpen(true)
  }, [])

  // Handle closing auth modal
  const handleCloseAuthModal = useCallback(() => {
    setAuthModalOpen(false)
  }, [])

  return (
    <>
      <SkipLink />
      <Navbar
        currentPage={getCurrentPage()}
        onNavigate={handleNavigate}
        onOpenAuthModal={handleOpenAuthModal}
      />
      {/* Sticky-footer shell: a min-h-screen flex column keeps the discreet
          AppFooter pinned to the bottom of the viewport on short pages and
          pushed below the content on long ones. `main` stays the a11y landmark.
          ⚠️ El `pt-20` que compensa la barra FIJA vive aquí, en la columna, y no
          en `main`: los avisos globales van dentro y necesitan ese hueco tanto
          como el contenido. Cuando colgaban por encima de la barra caían
          exactamente debajo de ella —invisibles e IMPULSABLES— y el único
          control que termina una suplantación dejaba de existir para el ratón.
          `box-sizing: border-box` (preflight de Tailwind) mantiene la altura
          mínima en 100vh: el relleno come contenido, no añade página. */}
      {/* `app-shell`: a page that must fit the viewport (the ledger, `.ledger-viewport`) bounds
          this column to it through CSS `:has()`; every other page keeps the sticky footer. */}
      <div className="app-shell flex min-h-screen flex-col pt-20">
        {/* Global warning banners */}
        <ImpersonationBanner />
        <WorkspaceBanner />
        <DeletionWarningBanner />
        <main id="main-content" tabIndex={-1} className="flex-1 focus:outline-none">
          {children}
        </main>
        <AppFooter />
      </div>
      <AuthModal
        isOpen={authModalOpen}
        onClose={handleCloseAuthModal}
        initialMode={authModalMode}
        onSuccess={handleCloseAuthModal}
      />
      {/* Issue #305 last-mile — floating panel surfaces active imports
          (with progress) AND recently completed imports that opened an
          unknown-rows auto-ticket (banner stays until the user dismisses
          it). Self-mounts SSE on the syncClient; renders nothing when
          there is no active or pending notification. */}
      <ImportProgressIndicator />
    </>
  )
}
